import { useQuery } from '@tanstack/react-query'
import z from 'zod'

import { useApi, useFormWithSchema } from '@fortem-monorepo/react-spa'
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogProps,
  DialogTitle,
  Form,
  FormError,
  FormField,
  FormItem,
  FormLabel,
  SearchSelect,
} from '@fortem-monorepo/react-ui'

import { useMarketSummaryContext } from '@/pages/instruments/market-summary/market-summary-context'
import { useAddInstrumentToDisplayMutation } from '@/pages/instruments/market-summary/market-summary.mutations'

const schema = z.object({ instrument_id: z.number().int().nonnegative() })

export interface AddInstrumentToDisplayDialogProps extends DialogProps {
  onClose: () => void
  mutationParams: { display_id: number }
}

export const AddInstrumentToDisplayDialog = ({
  mutationParams,
  onClose,
  ...props
}: AddInstrumentToDisplayDialogProps) => {
  const api = useApi()
  const { instrumentsQuery, hasWriteAccess, view } = useMarketSummaryContext()

  const membersQuery = useQuery({
    queryKey: ['market-summary', 'display-instruments', mutationParams.display_id],
    queryFn: () => api.marketSummary.display(mutationParams.display_id).instruments.listAll(),
    enabled: Boolean(props.open),
  })

  const form = useFormWithSchema({ mode: 'all' }, schema)

  const mutation = useAddInstrumentToDisplayMutation(mutationParams.display_id)

  const members = new Set((membersQuery.data ?? []).map((i) => i.instrument_id))

  const available = (instrumentsQuery.data ?? []).filter((i) => !members.has(i.instrument_id))

  const ready = membersQuery.isSuccess && instrumentsQuery.isSuccess && !membersQuery.isFetching

  const selected = form.watch('instrument_id')

  return (
    <Dialog
      {...props}
      onOpenChange={(open) => {
        if (!open && !mutation.isPending) onClose()
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add instrument to {view?.display_name}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <FormField
            control={form.control}
            name="instrument_id"
            render={({ field }) => (
              <FormItem>
                <div className="flex items-center gap-2">
                  <FormLabel>Instrument</FormLabel>
                  <FormError />
                </div>
                <SearchSelect
                  value={field.value?.toString()}
                  setValue={(value) => field.onChange(value === undefined ? undefined : Number(value))}
                  disabled={!ready || mutation.isPending}
                  loading={membersQuery.isFetching || instrumentsQuery.isFetching}
                  placeholder="Choose an instrument..."
                  options={available.map((i) => ({
                    value: String(i.instrument_id),
                    label: `${i.unique_identifier ?? i.instrument_id} - ${i.long_name ?? i.section}`,
                  }))}
                />
              </FormItem>
            )}
          />
        </Form>
        {(membersQuery.isError || instrumentsQuery.isError) && (
          <div role="alert" className="flex items-center gap-2 text-sm text-destructive">
            Unable to load instruments.
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                void membersQuery.refetch()
                void instrumentsQuery.refetch()
              }}
            >
              Retry
            </Button>
          </div>
        )}
        {ready && available.length === 0 && (
          <p className="text-sm text-muted-foreground">All registered instruments are already in this display.</p>
        )}
        <DialogFooter>
          <Button size="sm" variant="outline" disabled={mutation.isPending} onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={
              !ready || !hasWriteAccess || !form.formState.isValid || members.has(selected) || mutation.isPending
            }
            onClick={form.handleSubmit((params) => {
              if (!ready || !hasWriteAccess || members.has(params.instrument_id) || mutation.isPending) return
              mutation.mutate(params, { onSuccess: onClose })
            })}
          >
            {mutation.isPending ? 'Adding...' : 'Add'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
